import { useMemo } from 'react';
import useSupercluster from 'use-supercluster';

import type { Ad } from 'src/types/__generated__/graphql';

type BoundsWithZoom = { bounds: GeoJSON.BBox | undefined; zoom?: number };

type MarkerProps = {
    ad?: Ad;
    cluster?: {
        lat: number;
        lng: number;
        pointCount: number;
        id?: string | number;
        childrenIds?: string[];
        mapIsOnMaxZoom: boolean;
        handleClickCluster: () => void;
        getClusterChildren: () => ClusterType[] | null;
    };
};

type ClusterType = supercluster.PointFeature<{
    cluster: boolean;
    point_count: number;
    adId: string;
    ad: MarkerProps['ad'];
}>;

type Args = {
    ads: Ad[];
    boundsWithZoom?: BoundsWithZoom;
    disableRefresh: boolean;
    maxZoom?: number;
};

export const useClusters = ({ ads, boundsWithZoom, disableRefresh, maxZoom }: Args) => {
    const points: ClusterType[] | undefined = useMemo(
        () =>
            ads.map((ad) => ({
                type: 'Feature',
                properties: { cluster: false, adId: ad.id, ad, point_count: 1 },
                geometry: {
                    type: 'Point',
                    coordinates: [ad.proxyLng, ad.proxyLat],
                },
            })),
        [ads],
    );

    const { clusters, supercluster } = useSupercluster({
        points,
        bounds: boundsWithZoom?.bounds,
        zoom: boundsWithZoom?.zoom ?? 10,
        disableRefresh,
        options: {
            radius: 120, // Cluster radius, in pixels. @default 40
            maxZoom, // Maximum zoom level at which clusters are generated. @default 16
        },
    });

    return { clusters, supercluster };
};
